import Phaser from 'phaser';

// Two-pass text styling for score, free-turns count, and park name. First
// pass uses the SnackerComicOutline font in #F3F572 (the outline). Second
// pass renders the same string at the same origin in SnackerComic #DB1412
// (the fill). The outline font already carries the stroke shape, so no
// strokeStyle is set on either pass.
export interface TwoPassText {
  outline: Phaser.GameObjects.Text;
  fill: Phaser.GameObjects.Text;
  setText(s: string): void;
  setVisible(v: boolean): void;
  destroy(): void;
}

export interface TwoPassTextOpts {
  fontSize: string;
  originX?: number;
  originY?: number;
  depth?: number;
  wordWrapWidth?: number;
  align?: 'left' | 'center' | 'right';
  lineSpacing?: number;
}

export function makeTwoPassText(
  scene: Phaser.Scene,
  x: number,
  y: number,
  text: string,
  opts: TwoPassTextOpts,
): TwoPassText {
  const originX = opts.originX ?? 0.5;
  const originY = opts.originY ?? 0.5;
  // Phaser sizes its text canvas from the browser's measureText, which can
  // under-estimate the actual painted width for ornate fonts (the outline
  // glyph in particular extends past the nominal advance). Add horizontal
  // padding so the right edge isn't clipped. setOrigin then re-centres on
  // the now-wider canvas, which keeps the visual position unchanged.
  const padX = 16;
  const baseStyle: Phaser.Types.GameObjects.Text.TextStyle = {
    fontSize: opts.fontSize,
    padding: { x: padX, y: 0 },
  };
  if (opts.wordWrapWidth !== undefined) baseStyle.wordWrap = { width: opts.wordWrapWidth };
  if (opts.align !== undefined) baseStyle.align = opts.align;
  if (opts.lineSpacing !== undefined) {
    (baseStyle as { lineSpacing?: number }).lineSpacing = opts.lineSpacing;
  }
  const outline = scene.add
    .text(x, y, text, {
      ...baseStyle,
      fontFamily: 'SnackerComicOutline',
      color: '#F3F572',
    })
    .setOrigin(originX, originY);
  const fill = scene.add
    .text(x, y, text, {
      ...baseStyle,
      fontFamily: 'SnackerComic',
      color: '#DB1412',
    })
    .setOrigin(originX, originY);
  if (opts.depth !== undefined) {
    outline.setDepth(opts.depth);
    fill.setDepth(opts.depth + 0.1);
  }
  return {
    outline,
    fill,
    setText(s: string): void {
      outline.setText(s);
      fill.setText(s);
    },
    setVisible(v: boolean): void {
      outline.setVisible(v);
      fill.setVisible(v);
    },
    destroy(): void {
      outline.destroy();
      fill.destroy();
    },
  };
}
